import {useState} from 'react';
import {DownOutlined, UpOutlined} from '@ant-design/icons';
import {formatMoney} from 'utils/currencyFormatting';
import {cardUsd} from './swapReporting';
import styles from './swapTransactions.module.scss';

// Each card names its totals for what happened to those swaps: a pending
// swap is only requested, a failed one never converted.
const CARDS = [
  {
    key: 'all',
    status: undefined,
    tone: 'toneAll',
    label: 'All',
    from: 'Total from',
    into: 'Total into',
  },
  {
    key: 'pending',
    status: 'PENDING',
    tone: 'tonePending',
    label: 'Pending',
    from: 'Requested from',
    into: 'Requested into',
  },
  {
    key: 'successful',
    status: 'SUCCESSFUL',
    tone: 'toneSuccessful',
    label: 'Successful',
    from: 'Swapped from',
    into: 'Swapped into',
  },
  {
    key: 'failed',
    status: 'FAILED',
    tone: 'toneFailed',
    label: 'Failed',
    from: 'Attempted from',
    into: 'Failed to swap into',
  },
];

// One row per currency, code on the left and amount on the right, so a card
// with several currencies reads as a short list rather than a run-on line.
// Unlike currencies are never added together.
const Totals = ({label, totals}) =>
  totals?.length ? (
    <span className={styles.cardTotals}>
      <span className={styles.cardTotalsLabel}>{label}</span>
      {totals.map(total => (
        <span key={total.currency} className={styles.cardTotalRow}>
          <span className={styles.cardTotalCode}>{total.currency}</span>
          <span
            className={styles.cardTotal}
            title={formatMoney(total.amount, total.currency)}>
            {formatMoney(total.amount, total.currency)}
          </span>
        </span>
      ))}
    </span>
  ) : null;

/**
 * The four status cards, filled from one summary response. They are also the
 * status tabs: clicking one filters the list, while the cards themselves
 * ignore the status filter so all four stay populated.
 *
 * Each card leads with its USD amount and count. The per-currency totals are
 * open from the start; one toggle at the edge hides or shows them on every
 * card at once, so the row never ends up with cards of different heights.
 */
const SwapSummaryCards = ({
  summary,
  loading,
  error,
  activeStatus,
  onSelect,
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className={styles.summary}>
      <div className={styles.summaryHead}>
        <button
          type="button"
          className={styles.summaryToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}>
          {expanded ? 'Hide currency totals' : 'Show currency totals'}
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </button>
      </div>
      <div className={styles.statusTabs}>
        {CARDS.map(card => {
          const data = summary?.[card.key];
          const isActive = activeStatus === card.status;
          return (
            <button
              type="button"
              key={card.key}
              aria-pressed={isActive}
              className={[
                styles.statusTab,
                styles[card.tone],
                isActive && styles.statusTabActive,
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onSelect(card.status)}>
              <span className={styles.statusTabLabel}>{card.label}</span>
              {/* The money leads: how much is waiting, done or failed matters
                  more than how many swaps it is spread across. */}
              <span
                className={styles.statusTabAmount}
                title={
                  data ? `≈ ${cardUsd(data.normalizedUsd).full}` : undefined
                }>
                {data
                  ? `≈ ${cardUsd(data.normalizedUsd).short}`
                  : loading
                  ? '…'
                  : '–'}
              </span>
              <span className={styles.statusTabCount}>
                {data
                  ? `${data.count.toLocaleString('en-US')} swap${
                      data.count === 1 ? '' : 's'
                    }`
                  : '\u00a0'}
              </span>
              {data && (
                // Always rendered, so opening and closing can animate; the
                // collapsed panel is hidden from screen readers too.
                <span
                  className={`${styles.cardDetailsWrap} ${
                    expanded ? styles.cardDetailsOpen : ''
                  }`}
                  aria-hidden={!expanded}>
                  <span className={styles.cardDetailsInner}>
                    <span className={styles.cardDetails}>
                      {data.count > 0 ? (
                        <>
                          <Totals
                            label={card.from}
                            totals={data.sourceTotals}
                          />
                          <Totals
                            label={card.into}
                            totals={data.targetTotals}
                          />
                        </>
                      ) : (
                        <span className={styles.cardEmpty}>No swaps</span>
                      )}
                    </span>
                  </span>
                </span>
              )}
              {!data && error && card.key === 'all' && (
                <span className={styles.cardError}>{error}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SwapSummaryCards;
